import {
	FrontendAnalystViewSampleWithInstanceAndSpec,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendUserInfo,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import {
	ONE_DAY_IN_MS,
	TestHierarchyNodeType,
	TestStatus,
	TurnaroundTime,
} from "node-library/dist/constants";

import { NeutralColor, SemanticColor } from "node-library/dist/colors";

import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import { OrderIcon } from "../../../../common-components/svgIcons";
import {
	getShortTurnaroundTime,
	getTurnaroundTimeBackgroundColor,
} from "../../../../helpers/sampleHelper";
import {
	getTestStatusColors,
	getTestStatusForAnalyst,
	getTestStatusTranslationText,
} from "../../../../helpers/sampleInstanceHelper";
import {
	getEnabledHierarchyNodeTree,
	renderTestNodeSummary,
} from "../../../../helpers/testHierarchyHelper";

interface Props {
	testCategory: FrontendCompanyTestCategory;
	instance: FrontendAnalystViewSampleWithInstanceAndSpec;
	hierarchyNodeParentMap: Map<string, FrontendCompanyTestHierarchyNode[]>;
	user: FrontendUserInfo | undefined;
	goToViewInstanceDetails: () => void;
}

export default function AnalystViewInstanceRow(props: Props) {
	const { t: translation } = useTranslation();

	const {
		instance,
		user,
		goToViewInstanceDetails,
		testCategory,
		hierarchyNodeParentMap,
	} = props;

	const analystTestStatus: TestStatus = getTestStatusForAnalyst(instance);
	const statusString: string = getTestStatusTranslationText(
		analystTestStatus,
		translation,
	);
	const statusColor: {
		backgroundColor: string;
		borderColor: string;
		textColor: string;
	} = getTestStatusColors(analystTestStatus);

	//currently only used for admin view - submitted orders so always have the date
	const currDate: Date = new Date();
	const dueDate: Date = new Date(instance.expectedCompletionTime as Date);
	const dueDateString: string = formatCustomDate(dueDate);

	const remainingTimeInMillis =
		dueDate.getTime() - currDate.getTime() - ONE_DAY_IN_MS;
	const dueDateWarning: boolean = remainingTimeInMillis <= 0;

	const TATBackgroundColor: string = getTurnaroundTimeBackgroundColor(
		instance.sampleTestInfo?.turnaroundTime as TurnaroundTime,
	);

	const testNodeIds: Array<string> =
		instance.sampleTestInfo?.selectedTests.map((test) => {
			return test.selectedNodeId;
		}) || [];
	const hierarchyTreeRootNode: TestHierarchyTreeNode =
		getEnabledHierarchyNodeTree(testNodeIds, hierarchyNodeParentMap);

	const testOptionString: string =
		hierarchyTreeRootNode.children
			.filter((group) => group.nodeType === TestHierarchyNodeType.TEST_GROUP)
			.flatMap((group) => {
				let testString: Array<string> = [];
				// Render selected TESTs
				for (const child of group.children) {
					if (child.nodeType === TestHierarchyNodeType.TEST) {
						testString.push(renderTestNodeSummary(child));
					}
				}

				return testString;

				// Render the key of each true value
			})
			.join(", ") || "";
	const orderRowItem: React.ReactNode = (
		<div
			key={instance.sampleInstanceId}
			className="instance-list-view-row instance-list-view-item-row typography-body-01"
			onClick={() => {
				goToViewInstanceDetails();
			}}
		>
			<div
				className={`${
					dueDateWarning ? `typography-bold` : ``
				} instance-list-view-row-id-section instance-list-view-row-item`}
			>
				{dueDateWarning && (
					<span
						style={{
							width: "8px",
							height: "8px",
							borderRadius: "50%",
							backgroundColor: SemanticColor.ERROR,
						}}
					></span>
				)}
				{instance.sample.fullSampleId}
				<div
					className="typography-caption turnaround-time-box"
					style={{ backgroundColor: TATBackgroundColor }}
				>
					{getShortTurnaroundTime(
						instance.sampleTestInfo?.turnaroundTime as TurnaroundTime,
						translation,
					)}
				</div>
				{instance.isRetest ? (
					<div
						className="typography-caption turnaround-time-box"
						style={{ backgroundColor: TATBackgroundColor }}
					>
						{translation("Retest")}
					</div>
				) : (
					<></>
				)}
				{instance.isAdditionalTest ? (
					<div
						className="typography-caption turnaround-time-box"
						style={{ backgroundColor: TATBackgroundColor }}
					>
						{translation("Add")}
					</div>
				) : (
					<></>
				)}
			</div>
			<div className="instance-list-view-row-item">
				{instance.sample.isCustomMatrix
					? instance.sample.matrix
					: (
							testCategory.sampleMatrices.find((matrix) => {
								return matrix.matrixId == instance.sample.matrixId;
							}) as FrontendCompanySampleMatrix
						).subMatrix}
			</div>
			<div className="instance-list-view-row-item">
				{testOptionString}

				<div
					className="typography-caption"
					style={{
						display: "flex",
						gap: "5px",
						flexWrap: "wrap",
						color: NeutralColor.NEUTRAL_GREY_500,
					}}
				>
					{/* <div style={{ display: "flex", gap: "3px", alignItems: "end" }}>
						<UserIcon
							size={20}
							strokeColor={NeutralColor.NEUTRAL_GREY_500}
							strokeWidth={1.5}
						/>
						{order.orderedBy}
					</div> */}
				</div>
			</div>
			<div className="instance-list-view-row-item">
				{dueDateString.split(" | ")[0]}
			</div>
			<div className="instance-list-view-row-item">
				<div
					className="typography-caption admin-sample-status-box admin-status-box-medium"
					style={{
						backgroundColor: statusColor.backgroundColor,
						border: `1px solid ${statusColor.borderColor}`,
						color: statusColor.textColor,
					}}
				>
					{statusString}
				</div>
			</div>
			<div className="instance-list-view-row-item">
				<div style={{ display: "flex", justifyContent: "center", gap: "5px" }}>
					<div
						id={`view-order-${instance.sampleInstanceId}`}
						style={{ cursor: "pointer" }}
						onClick={(e) => {
							e.stopPropagation();
						}}
					>
						<OrderIcon size={20}></OrderIcon>
					</div>
				</div>
			</div>
		</div>
	);

	return orderRowItem;
}
